import PropTypes from "prop-types";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  InputLabel,
  TextField,
} from "@mui/material";
import { openSnackbar } from "api/snackbar";
import { createWholesaler, updateWholesaler } from "utils/apiHelper";
import { isValidGstNumber, normalizeGstNumber } from "utils/gst";

const toFormString = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
};

const getSupplierGstInitial = (data) => {
  if (!data) {
    return "";
  }
  const raw =
    data.gst_number ??
    data.gstin ??
    data.gst_no ??
    data.gst ??
    "";
  return toFormString(raw);
};

const formatWholesalerApiErrors = (data) => {
  if (!data || typeof data !== "object") {
    return null;
  }
  const parts = [];
  for (const [key, val] of Object.entries(data)) {
    if (key === "detail" && typeof val === "string") {
      parts.push(val);
      continue;
    }
    if (Array.isArray(val)) {
      parts.push(`${key}: ${val.join(" ")}`);
    } else if (typeof val === "string") {
      parts.push(`${key}: ${val}`);
    }
  }
  return parts.length ? parts.join(" ") : null;
};

export default function CreateSupplierForm({
  closeModal,
  mode = "create",
  supplierData = null,
  requireGst = false
}) {
  const isEdit = (mode === "edit");
  const gstRequired = !isEdit || requireGst;
  const SupplierSchema = Yup.object().shape({
    name: Yup.string().required("Supplier Name is required"),
    gst_number: gstRequired
      ? Yup.string()
          .required("GST Number is required")
          .test('gst-format', 'Invalid GST number format', (value) => isValidGstNumber(value))
      : Yup.string().test('gst-format', 'Invalid GST number format', (value) => !value || isValidGstNumber(value)),
    address: Yup.string(),
    phone_number: Yup.string().test('phone-format', 'Phone number must be 10 digits', (value) => !value || /^[0-9]{10}$/.test(value)),
  });
  const formik = useFormik({
    validationSchema: SupplierSchema,
    enableReinitialize: true,
    initialValues: {
      name: isEdit ? toFormString(supplierData?.name) : "",
      gst_number: isEdit ? getSupplierGstInitial(supplierData) : "",
      address: isEdit ? toFormString(supplierData?.address) : "",
      phone_number: isEdit
        ? toFormString(supplierData?.phone_number ?? supplierData?.phone)
        : "",
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        const gstNormalized = normalizeGstNumber(values.gst_number);
        const payload = {
          name: toFormString(values.name).trim(),
          address: toFormString(values.address).trim(),
          phone_number: toFormString(values.phone_number).trim(),
          gst_number: gstNormalized,
        };
        const response = (isEdit) ? await updateWholesaler(supplierData?.id, payload) : await createWholesaler(payload);
        const newSupplier = response?.name;
        openSnackbar({
          open: true,
          message: (isEdit) ? `Successfully updated supplier ${newSupplier}` : `Successfully added supplier ${newSupplier}`,
          variant: "alert",
          alert: { color: "success" },
        });
        closeModal();
      } catch (error) {
        const apiData = error.response?.data;
        const detail =
          typeof apiData?.detail === "string" ? apiData.detail : null;
        const fieldMsg = formatWholesalerApiErrors(apiData);
        const message =
          fieldMsg ||
          detail ||
          (isEdit ? "Failed to update supplier" : "Failed to add supplier");
        openSnackbar({
          open: true,
          message,
          variant: "alert",
          alert: { color: "error" },
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    getFieldProps,
  } = formik;

  return (
    <>
      <FormikProvider value={formik}>
        <Form autoComplete='off' noValidate onSubmit={handleSubmit}>
          <DialogTitle>
            {isEdit ? "Edit" : "Create New"} Supplier 
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ py: 2.5, px: 7 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel>Supplier Name <span style={{ color: "red" }}>*</span></InputLabel>
                  <TextField
                    fullWidth
                    placeholder='Enter Supplier Name'
                    {...getFieldProps("name")}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel>GST Number {gstRequired && <span style={{ color: "red" }}>*</span>}</InputLabel>
                  <TextField
                    fullWidth
                    placeholder='Enter GST Number (e.g., 27ABCDE1234F1Z5)'
                    {...getFieldProps("gst_number")}
                    error={Boolean(touched.gst_number && errors.gst_number)}
                    helperText={touched.gst_number && errors.gst_number}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel>Address</InputLabel>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder='Enter Complete Address'
                    {...getFieldProps("address")}
                    error={Boolean(touched.address && errors.address)}
                    helperText={touched.address && errors.address}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel>Phone Number</InputLabel>
                  <TextField
                    fullWidth
                    placeholder='Enter 10-digit Phone Number'
                    {...getFieldProps("phone_number")}
                    error={Boolean(touched.phone_number && errors.phone_number)}
                    helperText={touched.phone_number && errors.phone_number}
                  />
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider />
          <DialogActions  sx={{ py: 2.5, px: 2.5 }}>
            <Button color='secondary' onClick={closeModal}>
              Cancel
            </Button>
            <Button variant='contained' type='submit'>
              {isEdit ? "Update" : "Submit"}
            </Button>
          </DialogActions>
        </Form>
      </FormikProvider>
    </>
  );
}

CreateSupplierForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
};
